import React, { createContext, useContext, ReactNode } from 'react';

import {
  useLocalizedLabels,
  LocalizedLabelMap,
} from '../hooks/useLocalizedLabels';

interface LocalizationContextType {
  labelMap: LocalizedLabelMap;
  loading: boolean;
  getLabel: (defaultLabel: string) => string;
}

const LocalizationContext = createContext<LocalizationContextType | undefined>(
  undefined,
);

interface LocalizationProviderProps {
  scope: string;
  children: ReactNode;
}

/**
 * Provider component that fetches and provides localized labels for a given scope.
 * Wrap components that need localized labels with this provider.
 */
export const LocalizationProvider = ({
  scope,
  children,
}: LocalizationProviderProps) => {
  const { labelMap, loading } = useLocalizedLabels(scope);

  const getLabel = (defaultLabel: string): string => {
    return labelMap[defaultLabel] || defaultLabel;
  };

  return (
    <LocalizationContext.Provider value={{ labelMap, loading, getLabel }}>
      {children}
    </LocalizationContext.Provider>
  );
};

/**
 * Hook to access localized labels within a LocalizationProvider.
 * Returns a function to get localized labels.
 */
export const useLocalization = (): LocalizationContextType => {
  const context = useContext(LocalizationContext);
  if (!context) {
    throw new Error(
      'useLocalization must be used within a LocalizationProvider',
    );
  }
  return context;
};
